import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { createNote, deleteNote, listNotesForMatter } from '../../../actions/notes/notes';
import { generatePresignedUrls } from '../../../actions/files/generatePresignedUrls';
import { generateDownloadUrl } from '../../../actions/files/generateDownloadUrl';
import GenericDropzone, { DOCUMENT_ACCEPT } from '../../../components/forms/GenericDropzone';
import ContextDictationField from '../../../components/forms/ContextDictationField';
import type { Note, NoteAttachment, NoteAttachmentInput } from '../../../types/note';

interface NotesTabProps {
  matterId: string;
}

const MAX_ATTACHMENTS = 4;
const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const wordCount = (text: string): number =>
  text.trim().split(/\s+/).filter(Boolean).length;

const NotesTab: React.FC<NotesTabProps> = ({ matterId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listNotesForMatter(matterId);
      setNotes(result);
    } catch {
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    load();
  }, [load]);

  const onFilesDropped = useCallback(
    (accepted: File[]) => {
      setError('');
      setPendingFiles((prev) => {
        const oversize = accepted.find((f) => f.size > MAX_FILE_SIZE_BYTES);
        if (oversize) {
          setError(`"${oversize.name}" is larger than ${MAX_FILE_SIZE_MB} MB.`);
          return prev;
        }
        const combined = [...prev, ...accepted];
        if (combined.length > MAX_ATTACHMENTS) {
          setError(`A note can have at most ${MAX_ATTACHMENTS} attachments.`);
          return combined.slice(0, MAX_ATTACHMENTS);
        }
        return combined;
      });
    },
    [],
  );

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setError('');
  };

  const uploadPendingFiles = async (): Promise<NoteAttachmentInput[]> => {
    if (pendingFiles.length === 0) return [];
    const presigned = await generatePresignedUrls(
      pendingFiles.map((f) => ({ filename: f.name, contentType: f.type, size: f.size })),
    );
    await Promise.all(
      presigned.map((p, i) =>
        fetch(p.url, {
          method: 'PUT',
          body: pendingFiles[i],
          headers: { 'Content-Type': pendingFiles[i].type },
        }).then((res) => {
          if (!res.ok) throw new Error(`Upload failed for ${pendingFiles[i].name}`);
        }),
      ),
    );
    return presigned.map((p, i) => ({
      key: p.key,
      filename: pendingFiles[i].name,
      contentType: pendingFiles[i].type,
      size: pendingFiles[i].size,
    }));
  };

  const handleSave = async () => {
    if (!body.trim() && pendingFiles.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const attachments = await uploadPendingFiles();
      await createNote(matterId, body.trim(), attachments);
      setBody('');
      setPendingFiles([]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note: Note) => {
    try {
      await deleteNote(matterId, note.noteId);
      await load();
    } catch {
      setError('Failed to delete note.');
    }
  };

  const handleAttachmentDownload = async (att: NoteAttachment) => {
    try {
      const { downloadUrl } = await generateDownloadUrl(att.key, att.filename);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      setError(`Failed to download "${att.filename}".`);
    }
  };

  const canSave = (body.trim().length > 0 || pendingFiles.length > 0) && !saving;
  const remainingSlots = MAX_ATTACHMENTS - pendingFiles.length;

  return (
    <Box>
      <Box
        sx={{
          bgcolor: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--t1)', mb: 1.75, letterSpacing: '-0.005em' }}>
          Add Note
        </Typography>
        <ContextDictationField
          minRows={3}
          maxRows={8}
          value={body}
          onChange={setBody}
          placeholder="Record a call, strategy note, status update..."
          size="small"
        />

        {remainingSlots > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <GenericDropzone
              onDrop={onFilesDropped}
              maxFiles={remainingSlots}
              acceptOverride={DOCUMENT_ACCEPT}
              mainText="Attach files"
              subText={`Up to ${MAX_ATTACHMENTS} files per note · PDF, DOCX, XLSX, EML, MSG · ${MAX_FILE_SIZE_MB} MB each`}
              buttonText="Select files"
            />
          </Box>
        )}

        {pendingFiles.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {pendingFiles.map((file, i) => (
              <Box
                key={`${file.name}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.75,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <AttachFileIcon sx={{ fontSize: 14, color: 'var(--t3)' }} />
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      color: 'var(--t1)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'var(--t3)', flexShrink: 0, fontFamily: 'ui-monospace, monospace' }}>
                    {formatSize(file.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => removePendingFile(i)} title="Remove" sx={{ '&:hover': { color: 'var(--err)', bgcolor: 'color-mix(in srgb, var(--err) 8%, transparent)' }, borderRadius: '6px' }}>
                  <CloseIcon sx={{ fontSize: 14, color: 'var(--t3)' }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography sx={{ color: 'var(--t4)', fontSize: '0.72rem' }}>
            {wordCount(body)} word{wordCount(body) === 1 ? '' : 's'}
            {pendingFiles.length > 0 && ` · ${pendingFiles.length} file${pendingFiles.length === 1 ? '' : 's'}`}
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={!canSave}
            onClick={handleSave}
            sx={{
              bgcolor: 'var(--teal)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '8px',
              px: 2,
              py: 0.65,
              boxShadow: 'none',
              '&:hover': { bgcolor: 'var(--teal-mid, #2c3578)', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: 'var(--s2)', color: 'var(--t4)' },
            }}
          >
            {saving ? 'Saving...' : 'Save Note'}
          </Button>
        </Box>
        {error && (
          <Typography sx={{ color: 'var(--err)', fontSize: '0.78rem', mt: 1 }}>{error}</Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: 'var(--teal)' }} />
        </Box>
      ) : notes.length === 0 ? (
        <Typography sx={{ color: 'var(--t3)', fontSize: '0.82rem', textAlign: 'center', py: 4 }}>
          No notes yet.
        </Typography>
      ) : (
        notes.map((note) => {
          const attachments = note.attachments ?? [];
          return (
          <Box
            key={note.noteId}
            sx={{
              bgcolor: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              p: 2,
              mb: 1.25,
              transition: 'background-color 0.15s',
              '&:hover': { bgcolor: 'var(--s1)' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--t3)', fontFamily: 'ui-monospace, monospace' }}>
                {formatDate(note.createdAt)} · {wordCount(note.body)} word{wordCount(note.body) === 1 ? '' : 's'}
                {attachments.length > 0 && ` · ${attachments.length} file${attachments.length === 1 ? '' : 's'}`}
              </Typography>
              <IconButton size="small" onClick={() => handleDelete(note)} title="Delete" sx={{ color: 'var(--t3)', borderRadius: '6px', '&:hover': { color: 'var(--err)', bgcolor: 'color-mix(in srgb, var(--err) 8%, transparent)' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: 'var(--t1)', whiteSpace: 'pre-wrap' }}>
              {note.body}
            </Typography>
            {attachments.length > 0 && (
              <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {attachments.map((att) => (
                  <Box
                    key={att.key}
                    onClick={() => handleAttachmentDownload(att)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      px: 1.25,
                      py: 0.75,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      '&:hover': { bgcolor: 'var(--s1)', borderColor: 'var(--t3)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <AttachFileIcon sx={{ fontSize: 14, color: 'var(--t3)' }} />
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          color: 'var(--t1)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {att.filename}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'var(--t3)', flexShrink: 0 }}>
                        {formatSize(att.size)}
                      </Typography>
                    </Box>
                    <DownloadIcon sx={{ fontSize: 14, color: 'var(--t3)' }} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          );
        })
      )}
    </Box>
  );
};

export default NotesTab;
